'use client'

// libraries
import clsx from 'clsx'
import { useState, useEffect } from 'react'

// components
import Accordion, { AccordionList, PublicationData as AccordionPublicationData } from '/react4xp/components/Accordion'
import Breadcrumbs from '/react4xp/components/Breadcrumbs'
import Section from '/react4xp/components/Section'
import HighlightedPublication, { HighlightPublicationData } from '/react4xp/components/HighlightedPublication'
import Loading from '/react4xp/components/Loading'

// svg
import { Loading as LoadingIcon, Spinner } from '/react4xp/components/Icons/Ux'

// css
import './index.scss'

// types
interface HighlightPublicationsProps {
   className?: string
   pageTitle?: string
   mockData?: HighlightPublicationData[]
   howManyPerPage?: number
   breadcrumbs?: {
      label: string
      url?: string
   }[]
   singleStrings?: {
      loadMore?: string
      loading?: string
      morePublications?: string
   }
   services?: {
      getPublications?: string
   }
}

export default function HighlightPublications({
   mockData,
   howManyPerPage = 4,
   services,
   pageTitle,
   breadcrumbs,
   singleStrings,
}: HighlightPublicationsProps) {

   const [highlightedPublications, setHighlightedPublications] = useState<HighlightPublicationData[]>([])
   const [morePublications, setMorePublications] = useState<HighlightPublicationData[]>([])
   const [nextMoreOffset, setNextMoreOffset] = useState(howManyPerPage * 2)
   const [hasMore, setHasMore] = useState(false)
   const [isLoading, setIsLoading] = useState(false)
   const [isLoadingMore, setIsLoadingMore] = useState(false)

   async function handleFetchPublications() {
      setIsLoading(true)

      if (!services?.getPublications) {
         const all = mockData || []
         setHighlightedPublications(all.slice(0, howManyPerPage))
         setMorePublications(all.slice(howManyPerPage, howManyPerPage * 2))
         setHasMore(all.length > howManyPerPage * 2)
         setNextMoreOffset(howManyPerPage * 2)
         setIsLoading(false)
         return
      }

      try {
         const url = new URL(services.getPublications)
         const response = await fetch(url)

         if (!response.ok) {
            throw new Error(`Failed to fetch publications: ${response.statusText}`)
         }

         const data = await response.json()
         setHighlightedPublications(data.highlighted || [])
         setMorePublications(data.more || [])
         setHasMore(data.hasMore ?? false)
      } catch (err) {
         console.error('Error while fetching publications', err)
      } finally {
         setIsLoading(false)
      }
   }

   async function handleLoadMore() {
      setIsLoadingMore(true)

      if (!services?.getPublications) {
         const all = mockData || []
         const nextBatch = all.slice(nextMoreOffset, nextMoreOffset + howManyPerPage)
         const newOffset = nextMoreOffset + howManyPerPage
         setMorePublications(prev => [...prev, ...nextBatch])
         setHasMore(all.length > newOffset)
         setNextMoreOffset(newOffset)
         setIsLoadingMore(false)
         return
      }

      try {
         const url = new URL(services.getPublications)
         url.searchParams.set('offset', nextMoreOffset.toString())
         url.searchParams.set('pageSize', howManyPerPage.toString())
         const response = await fetch(url)

         if (!response.ok) {
            throw new Error(`Failed to fetch publications: ${response.statusText}`)
         }

         const data = await response.json()
         setMorePublications(prev => [...prev, ...data.hits])
         setHasMore(data.hasMore ?? false)
         setNextMoreOffset(prev => prev + howManyPerPage)
      } catch (err) {
         console.error('Error while fetching more publications', err)
      } finally {
         setIsLoadingMore(false)
      }
   }

   useEffect(() => {
      handleFetchPublications()
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   if (isLoading) {
      return <Loading spinner={<Spinner />} />
   }

   return (
      <div className={clsx('page-highlight-publications')}>
         {(breadcrumbs && breadcrumbs.length > 0 || pageTitle) && (
            <Section>
               <div className="container">
                  <div className="row">
                     <div className="col-12 col-md-8 offset-md-2 col-lg-6 offset-lg-3">
                        {breadcrumbs && breadcrumbs.length > 0 && (
                           <Breadcrumbs items={breadcrumbs} />
                        )}
                        {pageTitle && <h1><span className="h1">{pageTitle}</span></h1>}
                     </div>
                  </div>
               </div>
            </Section>
         )}

         {/* Highlighted publications — placeholder until HighlightedPublicationsList component is built */}
         <Section>
            <div className="container">
               <div className="row">
                  <div className="col-12">
                     <div className="page-highlight-publications__highlighted-publications">
                        {highlightedPublications.map((item, i) => (
                           <HighlightedPublication key={i} highlightPublicationData={item} />
                        ))}
                     </div>
                  </div>
               </div>
            </div>
         </Section>

         {/* More publications */}
         <Section>
            <div className="container">
               <div className="row">
                  <div className="col-lg-8 offset-lg-2">
                     <div className="page-highlight-publications__more-publications-header">
                        <p className="body">{singleStrings?.morePublications || 'More publications'}</p>
                     </div>
                     <AccordionList className='page-highlight-publications__more-publications' oneAtTime={false}>
                        {morePublications.map((item, i) => (
                           <Accordion
                              key={i}
                              title={item.title}
                              content={item.intro ? `<p>${item.intro}</p>` : undefined}
                              color='beige'
                              publicationData={item.publicationData}
                           />
                        ))}
                     </AccordionList>
                  </div>
               </div>
            </div>
         </Section>

         {hasMore && (
            <Section>
               <div className="container">
                  <div className="row">
                     <div className="col-12">
                        <div className="page-highlight-publications__load-more">
                           <button
                              className='button-hollow-black'
                              type='button'
                              onClick={handleLoadMore}
                              disabled={isLoadingMore}
                           >
                              <span className='button-hollow-black__text'>
                                 {singleStrings?.loadMore || 'Load more'}
                              </span>

                              {isLoadingMore && (
                                 <span className='button-hollow-black__loading'>
                                    <LoadingIcon />
                                 </span>
                              )}
                           </button>
                        </div>
                     </div>
                  </div>
               </div>
            </Section>
         )}
      </div>
   )
}
